import { InteractionType, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { loginRequest } from '../lib/auth';

export class SessionExpiredError extends Error {
	constructor(message = 'Your session has expired, redirecting to sign in...') {
		super(message);
		this.name = 'SessionExpiredError';
	}
}

const redirectToLogin = (instance: IPublicClientApplication) => {
	instance
		.loginRedirect({ ...loginRequest, prompt: 'login' })
		.catch((redirectError) => console.error(redirectError));
};

export const useAuth = (msalRequest: { scopes: string[] }) => {
	const { instance } = useMsal();
	const activeAccount = instance.getActiveAccount();

	const { result, error } = useMsalAuthentication(InteractionType.Popup, {
		...msalRequest,
		account: activeAccount!,
		redirectUri: '/',
	});

	const accessToken = result?.accessToken;

	const signIn = useCallback(() => {
		redirectToLogin(instance);
	}, [instance]);

	// msal could not (re)acquire a token silently, so the session is gone
	useEffect(() => {
		if (error) {
			signIn();
		}
	}, [error, signIn]);

	const authFetch = useCallback(
		async (input: RequestInfo | URL, init: RequestInit = {}) => {
			if (!accessToken) {
				if (!instance.getActiveAccount() || error) {
					signIn();
					throw new SessionExpiredError();
				}

				throw new SessionExpiredError(
					'Signing in, please try again in a moment'
				);
			}

			const response = await fetch(input, {
				...init,
				headers: {
					...init.headers,
					Authorization: `Bearer ${accessToken}`,
				},
			});

			if (response.status === 401 || response.status === 403) {
				signIn();
				throw new SessionExpiredError();
			}

			return response;
		},
		[accessToken, error, instance, signIn]
	);

	return { accessToken, isAuthenticated: !!accessToken, signIn, authFetch };
};

/**
 * Re-validates the session on page load and on every route change, so an
 * expired session sends the user to login instead of failing silently.
 */
export const useSessionGuard = () => {
	const { instance } = useMsal();
	const { pathname } = useLocation();

	useEffect(() => {
		const account = instance.getActiveAccount();
		if (!account) {
			return;
		}

		let cancelled = false;
		instance
			.acquireTokenSilent({ scopes: [], account })
			.catch(() => {
				if (!cancelled) {
					redirectToLogin(instance);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [instance, pathname]);
};

export type AuthFetch = ReturnType<typeof useAuth>['authFetch'];
